import { useCallback, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import './ReverseBalancePage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: 'Ters Bakiye Kontrolu ve Duzeltmeler',
    description: 'Mizan ters bakiye veren hesaplari tespit eder ve sistem virman fisleri uretir. trial_balance_entries degistirilmez.',
    analyzeRunning: 'Analiz yapiliyor...',
    runAnalyze: 'Ters Bakiye Analizi Calistir',
    applying: 'Uygulaniyor...',
    applyFixes: 'Duzeltmeleri Uygula ({{count}} hesap)',
    rollback: 'Geri Al',
    rollbackConfirm: 'Ters bakiye duzeltmeleri geri alinacak. Devam edilsin mi?',
    analysisFailed: 'Analiz basarisiz.',
    analysisError: 'Analiz sirasinda hata olustu.',
    applyFailed: 'Duzeltme uygulanamadi.',
    applyError: 'Duzeltme sirasinda hata olustu.',
    rollbackFailed: 'Geri alma basarisiz.',
    rollbackError: 'Geri alma sirasinda hata olustu.',
    vouchersCreated: '{{voucherCount}} fis olusturuldu, {{lineCount}} satir. Kebir mizan yeniden hesaplandi.',
    summaryLabelTotal: 'Toplam ters bakiye',
    summaryLabelWithRule: 'Kural tanimli',
    summaryLabelManual: 'Manuel inceleme',
    findingsTitle: 'Bulgu Listesi',
    colAccountCode: 'Hesap Kodu',
    colAccountName: 'Hesap Adi',
    colExpected: 'Beklenen',
    colActual: 'Gerceklesen',
    colNet: 'Net Tutar',
    colTarget: 'Hedef Hesap',
    colStatus: 'Durum',
    colDescription: 'Aciklama',
    statusRule: 'Kural var',
    statusManual: 'Manuel',
    noFindings: 'Ters bakiye tespit edilmedi.',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    title: 'Reverse Balance Check and Fixes',
    description: 'Detects reverse-balance accounts and creates system transfer vouchers. trial_balance_entries are not modified.',
    analyzeRunning: 'Running analysis...',
    runAnalyze: 'Run Reverse Balance Analysis',
    applying: 'Applying...',
    applyFixes: 'Apply Fixes ({{count}} accounts)',
    rollback: 'Rollback',
    rollbackConfirm: 'Reverse balance fixes will be rolled back. Continue?',
    analysisFailed: 'Analysis failed.',
    analysisError: 'An error occurred during analysis.',
    applyFailed: 'Fix could not be applied.',
    applyError: 'An error occurred during apply.',
    rollbackFailed: 'Rollback failed.',
    rollbackError: 'An error occurred during rollback.',
    vouchersCreated: '{{voucherCount}} vouchers created, {{lineCount}} lines. Trial balance recalculated.',
    summaryLabelTotal: 'Total reverse balances',
    summaryLabelWithRule: 'Rule defined',
    summaryLabelManual: 'Manual review',
    findingsTitle: 'Findings',
    colAccountCode: 'Account Code',
    colAccountName: 'Account Name',
    colExpected: 'Expected',
    colActual: 'Actual',
    colNet: 'Net Amount',
    colTarget: 'Target Account',
    colStatus: 'Status',
    colDescription: 'Description',
    statusRule: 'Rule exists',
    statusManual: 'Manual',
    noFindings: 'No reverse balance detected.',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

function ReverseBalancePage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    if (value == null || Number.isNaN(Number(value))) return currencyFormatter.format(0);
    return currencyFormatter.format(Number(value));
  }, [currencyFormatter]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [analysis, setAnalysis] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const { canWrite } = useCompanyWriteAccess(selection.companyId);

  const runAnalysis = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return;
    setLoading(true);
    setError('');
    setApplyResult(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/financials/reverse-balance/analyze?companyId=${selection.companyId}&periodId=${selection.periodId}`,
        { headers: buildHeaders() }
      );
      if (!res.ok) {
        const textError = await res.text();
        throw new Error(textError || t('analysisFailed'));
      }
      const data = await res.json();
      setAnalysis(data);
    } catch (e) {
      setError(e?.message || t('analysisError'));
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, t]);

  const handleApply = useCallback(async () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    if (!selection.companyId || !selection.periodId) return;
    setProcessing(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/financials/reverse-balance/apply?companyId=${selection.companyId}&periodId=${selection.periodId}`,
        { method: 'POST', headers: buildHeaders() }
      );
      if (!res.ok) {
        let msg = t('applyFailed');
        try {
          const json = await res.json();
          if (json?.message) msg = json.message;
        } catch {
          const textError = await res.text();
          if (textError) msg = textError;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setApplyResult(data);
      runAnalysis();
    } catch (e) {
      setError(e?.message || t('applyError'));
    } finally {
      setProcessing(false);
    }
  }, [canWrite, selection.companyId, selection.periodId, runAnalysis, t]);

  const handleRollback = useCallback(async () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    if (!selection.companyId || !selection.periodId) return;
    if (!window.confirm(t('rollbackConfirm'))) return;
    setProcessing(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/financials/reverse-balance/rollback?companyId=${selection.companyId}&periodId=${selection.periodId}`,
        { method: 'POST', headers: buildHeaders() }
      );
      if (!res.ok) throw new Error(t('rollbackFailed'));
      setApplyResult(null);
      runAnalysis();
    } catch (e) {
      setError(e?.message || t('rollbackError'));
    } finally {
      setProcessing(false);
    }
  }, [canWrite, selection.companyId, selection.periodId, runAnalysis, t]);

  const findings = analysis?.findings ?? [];
  const withRule = findings.filter((f) => f.hasRule);

  return (
    <div className="reverse-balance">
      <PageHeader title={t('title')} description={t('description')} />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <section className="reverse-balance__actions">
        <button type="button" className="btn-primary" onClick={runAnalysis} disabled={!selection.companyId || !selection.periodId || loading}>
          {loading ? t('analyzeRunning') : t('runAnalyze')}
        </button>
        {withRule.length > 0 && (
          <button type="button" className="btn-primary" onClick={handleApply} disabled={!canWrite || processing}>
            {processing ? t('applying') : t('applyFixes').replace('{{count}}', String(withRule.length))}
          </button>
        )}
        {(applyResult?.vouchersCreated > 0 || (analysis?.activeCorrectionVoucherCount ?? 0) > 0) && (
          <button type="button" className="btn-secondary" onClick={handleRollback} disabled={!canWrite || processing}>{t('rollback')}</button>
        )}
      </section>

      {error && <p className="reverse-balance__error">{error}</p>}

      {applyResult && applyResult.vouchersCreated > 0 && (
        <div className="reverse-balance__success">
          {t('vouchersCreated')
            .replace('{{voucherCount}}', String(applyResult.vouchersCreated))
            .replace('{{lineCount}}', String(applyResult.linesCreated))}
        </div>
      )}

      {analysis && (
        <section className="reverse-balance__summary">
          <p>
            <strong>{t('summaryLabelTotal')}:</strong> {analysis.totalCount} | {t('summaryLabelWithRule')}: {analysis.withRuleCount} | {t('summaryLabelManual')}: {analysis.needsManualReviewCount}
          </p>
        </section>
      )}

      {analysis && findings.length > 0 && (
        <section className="reverse-balance__table-wrapper">
          <h3>{t('findingsTitle')}</h3>
          <table className="reverse-balance__table">
            <thead>
              <tr>
                <th>{t('colAccountCode')}</th>
                <th>{t('colAccountName')}</th>
                <th>{t('colExpected')}</th>
                <th>{t('colActual')}</th>
                <th className="reverse-balance__align-right">{t('colNet')}</th>
                <th>{t('colTarget')}</th>
                <th>{t('colStatus')}</th>
                <th>{t('colDescription')}</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={`${f.accountCode}-${i}`}>
                  <td>{f.accountCode}</td>
                  <td>{f.accountName}</td>
                  <td>{f.expectedNormalSide}</td>
                  <td>{f.actualNetSide}</td>
                  <td className="reverse-balance__align-right">{formatCurrency(f.netAmount)}</td>
                  <td>{f.targetAccountCode ?? '-'}</td>
                  <td>
                    <span className={f.hasRule ? 'reverse-balance__tag ok' : 'reverse-balance__tag manual'}>
                      {f.hasRule ? t('statusRule') : t('statusManual')}
                    </span>
                  </td>
                  <td>{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {analysis && findings.length === 0 && (
        <div className="reverse-balance__empty"><p>{t('noFindings')}</p></div>
      )}
    </div>
  );
}

export default ReverseBalancePage;
